/**
 * The CDF row-identity contract — the ONE place CDF instance-identity
 * specifics live in the engine. A CDF instance reference (`{space,
 * externalId}`) is the value that identifies a knowledge-graph row, and
 * {@link CDF_INSTANCE_IDENTITY} packages how to recognise, compare, key, and
 * scope it behind the provider-agnostic {@link RowIdentityType} contract.
 *
 * Recognition and keying delegate to the SSOTs in `schema/utils.ts`
 * ({@link isInstanceReference} / {@link instanceKey}); no other engine module
 * re-derives instance-reference structure. The composed
 * {@link CDF_IDENTITY_TABLE} is threaded onto the eval / type-check contexts
 * at the provider/session boundary so the type-check pass recognises
 * direct-relation cells and the runtime dedups them by identity.
 */

import { createIdentityTable, type IdentityTable, type RowIdentityType } from '#core/lib/expr/schema/row-identity.ts';
import type { InstanceIdType } from '#core/schema/defs.ts';
import { instanceKey, isInstanceReference } from '#core/schema/utils.ts';

/**
 * The stable id of the CDF instance-identity contract. Surfaced on a
 * `row-identity` ColumnType (`identity.id`) and used to resolve the contract
 * back from an {@link IdentityTable} at fetch/translate time.
 */
export const CDF_INSTANCE_IDENTITY_ID = 'cdf-instance';

/**
 * The CDF instance-reference {@link RowIdentityType}. `is` is the
 * {@link isInstanceReference} SSOT guard; `equals` / `key` flatten through the
 * {@link instanceKey} SSOT; `scopeId` is the reference's `space` — the CDF
 * partition a relation target belongs to.
 */
export const CDF_INSTANCE_IDENTITY: RowIdentityType = {
  id: CDF_INSTANCE_IDENTITY_ID,
  is: isInstanceReference,
  equals: (a, b) => isInstanceReference(a) && isInstanceReference(b) && instanceKey(a) === instanceKey(b),
  key: (value) => instanceKey(value as InstanceIdType),
  scopeId: (value) => (value as InstanceIdType).space,
};

/**
 * The composed {@link IdentityTable} carrying the CDF identity contract. The
 * provider/session boundary threads this onto the eval and static type-check
 * contexts so identity recognition is uniform across passes.
 */
export const CDF_IDENTITY_TABLE: IdentityTable = createIdentityTable([CDF_INSTANCE_IDENTITY]);
